import React from "react";
import { ModalProps } from "react-bootstrap";
import { Plus, Trash, X } from "react-bootstrap-icons";
import { useFormik } from "formik";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import {
  BaseButton,
  Error,
  IMemberPlan,
  Switch,
  TextAreaInput,
  TextInput,
  selectTheme,
} from "../../../libs";
import { ReferralProgram, ReferralProgramFormValues } from "../types";
import * as Styled from "./ReferralProgramModal.styled";

interface CouponOption {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

interface Props extends ModalProps {
  program: ReferralProgram | null;
  plans: IMemberPlan[];
  coupons: CouponOption[];
  onSave: (values: Record<string, unknown>) => Promise<void>;
}

const isHttpUrl = (value: string) => {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

export const ReferralProgramModal: React.FC<Props> = ({
  program,
  plans,
  coupons,
  onSave,
  ...props
}) => {
  const formik = useFormik<ReferralProgramFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: program?.name || "",
      description: program?.description || "",
      terms: program?.terms || "",
      visibility: program?.visibility || "PUBLIC",
      attributionModel: program?.attributionModel || "LAST_TOUCH",
      appliesToAllPlans: program?.appliesToAllPlans ?? true,
      eligiblePlanIds: program?.eligiblePlanIds || [],
      destinationUrls: program?.destinationUrls?.length ? program.destinationUrls : [""],
      couponId: program?.couponId || "",
      startAt: program?.startAt ? new Date(program.startAt) : null,
      endAt: program?.endAt ? new Date(program.endAt) : null,
      status: program?.status || "ACTIVE",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name.trim()) errors.name = "* Program name is required";
      if (!values.description.trim()) errors.description = "* Description is required";
      if (!values.appliesToAllPlans && values.eligiblePlanIds.length === 0)
        errors.eligiblePlanIds = "* Select at least one plan";
      const invalidUrl = values.destinationUrls.find((url) => url.trim() && !isHttpUrl(url.trim()));
      if (invalidUrl) errors.destinationUrls = "* URLs must begin with http:// or https://";
      if (values.startAt && values.endAt && values.endAt <= values.startAt)
        errors.endAt = "* End date must be after the start date";
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        await onSave({
          ...values,
          destinationUrls: values.destinationUrls.map((url) => url.trim()).filter(Boolean),
          startAt: values.startAt?.toISOString() || null,
          endAt: values.endAt?.toISOString() || null,
        });
        helpers.resetForm();
        props.onHide?.();
      } catch {
        // The container reports the API error and the modal stays open for correction or retry.
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const planOptions = plans.map((plan) => ({ value: plan.id, label: plan.name }));
  const couponOptions = [
    { value: "", label: "No automatic coupon" },
    ...coupons
      .filter((coupon) => coupon.active)
      .map((coupon) => ({ value: coupon.id, label: `${coupon.code} — ${coupon.name}` })),
  ];
  const visibilityOptions = [
    { value: "PUBLIC", label: "Shown publicly" },
    { value: "HIDDEN", label: "Hidden / invitation only" },
  ];
  const attributionOptions = [
    { value: "LAST_TOUCH", label: "Latest referral wins until purchase" },
    { value: "FIRST_TOUCH", label: "First referral wins until purchase" },
  ];

  return (
    <Styled.ProgramModal {...props} centered keyboard={false}>
      <Styled.ModalWrapper>
        <Styled.Header>
          <Styled.ModalTitle>
            {program ? "Edit Referral Program" : "Create Referral Program"}
          </Styled.ModalTitle>
          <Styled.CloseButton
            onClick={() => {
              formik.resetForm();
              props.onHide?.();
            }}
          >
            <X size={22} />
          </Styled.CloseButton>
        </Styled.Header>
        <form onSubmit={formik.handleSubmit}>
          <Styled.FormGrid>
            <Styled.FullWidth>
              {formik.touched.name && formik.errors.name && <Error message={formik.errors.name} />}
              <TextInput
                fieldName="name"
                fieldLabel="Program Name *"
                placeholder="Enter program name"
                fieldValue={formik.values.name}
                inputLimit={100}
                onChangeHandler={formik.handleChange}
                onBlurHandler={formik.handleBlur as any}
                hasError={!!(formik.touched.name && formik.errors.name)}
              />
            </Styled.FullWidth>
            <Styled.FullWidth>
              <Styled.FieldLabel>Visibility</Styled.FieldLabel>
              <Select
                styles={selectTheme.singleSelect()}
                options={visibilityOptions}
                value={visibilityOptions.find(
                  (option) => option.value === formik.values.visibility
                )}
                onChange={(option) => formik.setFieldValue("visibility", option?.value)}
              />
            </Styled.FullWidth>
            <Styled.FullWidth>
              {formik.touched.description && formik.errors.description && (
                <Error message={formik.errors.description} />
              )}
              <TextAreaInput
                fieldName="description"
                fieldLabel="Description *"
                placeholder="Explain how the program works"
                fieldValue={formik.values.description}
                onChangeHandler={formik.handleChange}
                rows={3}
              />
            </Styled.FullWidth>
            <Styled.FullWidth>
              <TextAreaInput
                fieldName="terms"
                fieldLabel="Terms and Reward Notes"
                placeholder="Add terms or manual payout notes"
                fieldValue={formik.values.terms}
                onChangeHandler={formik.handleChange}
                rows={3}
              />
            </Styled.FullWidth>
            <Styled.Field>
              <Styled.FieldLabel>Attribution Rule</Styled.FieldLabel>
              <Select
                styles={selectTheme.singleSelect()}
                options={attributionOptions}
                value={attributionOptions.find(
                  (option) => option.value === formik.values.attributionModel
                )}
                onChange={(option) => formik.setFieldValue("attributionModel", option?.value)}
              />
            </Styled.Field>
            <Styled.Field>
              <Styled.FieldLabel>Automatic Coupon</Styled.FieldLabel>
              <Select
                styles={selectTheme.singleSelect()}
                options={couponOptions}
                value={couponOptions.find((option) => option.value === formik.values.couponId)}
                onChange={(option) => formik.setFieldValue("couponId", option?.value || "")}
              />
            </Styled.Field>
            <Styled.Field>
              <Styled.FieldLabel>Start Date and Time</Styled.FieldLabel>
              <Styled.DatePickerWrapper>
                <Styled.CustomDatePicker
                  selected={formik.values.startAt}
                  onChange={(date: Date | null) => formik.setFieldValue("startAt", date)}
                  showTimeSelect
                  dateFormat="Pp"
                  placeholderText="Start immediately"
                  isClearable
                />
              </Styled.DatePickerWrapper>
            </Styled.Field>
            <Styled.Field>
              {formik.errors.endAt && <Error message={formik.errors.endAt} />}
              <Styled.FieldLabel>End Date and Time</Styled.FieldLabel>
              <Styled.DatePickerWrapper>
                <Styled.CustomDatePicker
                  selected={formik.values.endAt}
                  onChange={(date: Date | null) => formik.setFieldValue("endAt", date)}
                  minDate={formik.values.startAt || new Date()}
                  showTimeSelect
                  dateFormat="Pp"
                  placeholderText="No end date"
                  isClearable
                />
              </Styled.DatePickerWrapper>
            </Styled.Field>
            <Styled.FullWidth>
              {formik.errors.destinationUrls && (
                <Error message={String(formik.errors.destinationUrls)} />
              )}
              <Styled.FieldLabel>Approved Destination URLs</Styled.FieldLabel>
              {formik.values.destinationUrls.map((url, index) => (
                <Styled.UrlRow key={index}>
                  <Styled.UrlInput
                    name={`destinationUrls.${index}`}
                    value={url}
                    onChange={formik.handleChange}
                    placeholder="https://example.com/watch"
                  />
                  <Styled.IconButton
                    type="button"
                    onClick={() =>
                      formik.setFieldValue(
                        "destinationUrls",
                        formik.values.destinationUrls.filter((_, itemIndex) => itemIndex !== index)
                      )
                    }
                    disabled={formik.values.destinationUrls.length === 1}
                  >
                    <Trash />
                  </Styled.IconButton>
                </Styled.UrlRow>
              ))}
              <BaseButton
                size="sm"
                variant="secondary"
                onClick={() =>
                  formik.setFieldValue("destinationUrls", [...formik.values.destinationUrls, ""])
                }
              >
                <Plus /> Add another URL
              </BaseButton>
              <Styled.HelpText>
                Query strings and hashes are removed when the program is saved.
              </Styled.HelpText>
            </Styled.FullWidth>
            <Styled.FullWidth>
              <Styled.SwitchRow>
                <Styled.SwitchLabel>Apply to All Membership Plans</Styled.SwitchLabel>
                <Switch
                  checked={formik.values.appliesToAllPlans}
                  onChange={(event) =>
                    formik.setFieldValue("appliesToAllPlans", event.target.checked)
                  }
                />
              </Styled.SwitchRow>
            </Styled.FullWidth>
            <Styled.FullWidth>
              <Styled.SwitchRow>
                <Styled.SwitchLabel>Program Active</Styled.SwitchLabel>
                <Switch
                  checked={formik.values.status === "ACTIVE"}
                  onChange={(event) =>
                    formik.setFieldValue("status", event.target.checked ? "ACTIVE" : "STOPPED")
                  }
                />
              </Styled.SwitchRow>
            </Styled.FullWidth>
            {!formik.values.appliesToAllPlans && (
              <Styled.FullWidth>
                {formik.errors.eligiblePlanIds && (
                  <Error message={String(formik.errors.eligiblePlanIds)} />
                )}
                <Styled.FieldLabel>Eligible Plans</Styled.FieldLabel>
                <Select
                  isMulti
                  styles={selectTheme.multiSelect(!!formik.errors.eligiblePlanIds)}
                  options={planOptions}
                  value={planOptions.filter((option) =>
                    formik.values.eligiblePlanIds.includes(option.value)
                  )}
                  onChange={(options) =>
                    formik.setFieldValue(
                      "eligiblePlanIds",
                      options.map((option) => option.value)
                    )
                  }
                  placeholder="Select membership plans"
                />
              </Styled.FullWidth>
            )}
          </Styled.FormGrid>
          <Styled.Footer>
            <Styled.ButtonSlot>
              <BaseButton
                variant="secondary"
                onClick={() => {
                  formik.resetForm();
                  props.onHide?.();
                }}
              >
                Cancel
              </BaseButton>
            </Styled.ButtonSlot>
            <Styled.ButtonSlot>
              <BaseButton type="submit" disabled={formik.isSubmitting}>
                {formik.isSubmitting ? "Saving..." : program ? "Save Changes" : "Create Program"}
              </BaseButton>
            </Styled.ButtonSlot>
          </Styled.Footer>
        </form>
      </Styled.ModalWrapper>
    </Styled.ProgramModal>
  );
};
